import { useEffect, useState } from 'react';
import type { RedDogLegal, RedDogOutcome, RedDogView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, useNarrow } from './common';

/** What a win pays per unit staked for a spread of `n` ranks (mirrors the engine's pay table). */
const payFor = (n: number) => (n <= 1 ? 5 : n === 2 ? 4 : n === 3 ? 2 : 1);

export function RedDogTable() {
  const t = useT();
  const view = useGame((s) => s.view) as RedDogView;
  const legal = useGame((s) => s.legal) as RedDogLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const narrow = useNarrow();
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = Boolean(myTurn && legal?.kind === 'bet');
  const deciding = Boolean(myTurn && legal?.kind === 'decide');
  const [amount, setAmount] = useState<number>(view.config.minBet);
  const [raise, setRaise] = useState(0);
  const ended = view.phase === 'HAND_END';
  const dealing = view.phase === 'DEALING' || view.phase === 'DRAWING';
  const myResult = view.lastResult?.seats.find((r) => r.seat === mySeat);
  const size = view.spreadSize;

  useEffect(() => {
    if (legal?.kind === 'bet') setAmount((a) => Math.max(legal.min, Math.min(a, legal.max)));
  }, [legal, view.handNo]);
  useEffect(() => {
    if (legal?.kind === 'decide') setRaise(legal.maxRaise);
  }, [legal, view.handNo]);

  const quick = legal?.kind === 'bet' ? [...new Set([1, 2, 5, 10, 25].map((k) => legal.min * k).filter((v) => v <= legal.max).concat(legal.max))].sort((a, b) => a - b) : [];
  const raises = legal?.kind === 'decide' && legal.maxRaise > 0 ? [...new Set([Math.ceil(legal.maxRaise / 4), Math.ceil(legal.maxRaise / 2), legal.maxRaise])] : [];
  const card = (id: string | null | undefined) => (id ? <PlayingCard id={id} size={narrow ? 'sm' : 'md'} /> : <span className="pcard sm" style={{ opacity: 0.25, border: '2px dashed currentColor', borderRadius: 8, display: 'inline-block', width: 44, height: 62 }} aria-hidden />);
  const outcomeText = (o: RedDogOutcome | null, delta: number) =>
    o === 'WIN' ? t('rdg.outWin', { n: delta }) : o === 'TRIPS' ? t('rdg.outTrips', { n: delta }) : o === 'LOSE' ? t('rdg.outLose', { n: -delta }) : t('rdg.outPush');
  const spreadLabel = size === null ? '' : size < 0 ? t('rdg.pair') : size === 0 ? t('rdg.consecutive') : t('rdg.spread', { n: size, p: payFor(size) });

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer">
          <div className="card glass col" style={{ padding: 8, gap: 6, alignItems: 'center' }} aria-label={t('rdg.spreadTitle')}>
            <strong>{t('rdg.spreadTitle')}</strong>
            <div className="row" style={{ gap: 10, minHeight: narrow ? 64 : 84, alignItems: 'center', justifyContent: 'center' }}>
              {card(view.spread?.[0])}
              <div className="col" style={{ alignItems: 'center', gap: 2 }} aria-label={t('rdg.thirdCard')}>
                {card(view.third)}
                <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('rdg.thirdCard')}</span>
              </div>
              {card(view.spread?.[1])}
            </div>
            {view.spread && <span className="center-note">{spreadLabel}</span>}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 8, minHeight: 28, flexWrap: 'wrap' }}>
            {ended && myResult && (
              <span className={`pill ${myResult.delta > 0 ? 'accent' : myResult.delta < 0 ? 'danger' : ''}`} role="status">
                {outcomeText(myResult.outcome, myResult.delta)}
              </span>
            )}
            {dealing && <span className="center-note">{t('ab.dealing')}</span>}
            {view.phase === 'WAITING' && <span className="center-note">{t('bj.waiting')}</span>}
            {view.phase === 'BETTING' && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('table.waitingFor', { name: name(turn.seats[0]) })}</span>}
            {view.phase === 'DECIDING' && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('rdg.waitDecide', { name: name(turn.seats[0]) })}</span>}
          </div>
          <div className="card glass col" style={{ padding: 8, gap: 6 }} aria-label={t('rdg.bets')}>
            {view.players.map((p) => {
              if (!p || p.status !== 'BET') return null;
              const res = view.lastResult?.seats.find((r) => r.seat === p.seat);
              return (
                <div key={p.seat} className="row" style={{ gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)', minWidth: 70 }}>{p.seat === mySeat ? t('cg.you') : name(p.seat)}</span>
                  <span className="pill" style={{ fontSize: 'var(--fs-xs)' }}>
                    {p.bet}
                    {p.raise > 0 ? ` + ${p.raise}` : ''}
                  </span>
                  {view.phase === 'DECIDING' && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{p.decided ? (p.raise > 0 ? t('rdg.raised') : t('rdg.stayed')) : ''}</span>}
                  {ended && res && <span className={`pill ${res.delta > 0 ? 'accent' : res.delta < 0 ? 'danger' : ''}`} style={{ fontSize: 'var(--fs-xs)' }}>{res.delta > 0 ? '+' : ''}{res.delta}</span>}
                </div>
              );
            })}
          </div>
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('rdg.ante')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('bj.placeBet')}>
              {quick.map((v) => (
                <button key={v} role="radio" aria-checked={amount === v} className={`bid-chip ${amount === v ? 'on' : ''}`} onClick={() => setAmount(v)}>
                  {v}
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', amount })}>
                {t('rdg.betAnte', { n: amount })}
              </button>
            </div>
          </div>
        )}
        {deciding && legal?.kind === 'decide' && me && (
          <div className="bid-picker" role="group" aria-label={t('rdg.decide')}>
            <div className="bid-head">
              <strong>{t('rdg.decide')}</strong>
              <span className="dim">{t('rdg.decideHint', { n: legal.spread, p: payFor(legal.spread) })}</span>
            </div>
            {raises.length > 1 && (
              <div className="bid-chips amounts" role="radiogroup" aria-label={t('rdg.raiseBy')}>
                {raises.map((v) => (
                  <button key={v} role="radio" aria-checked={raise === v} className={`bid-chip ${raise === v ? 'on' : ''}`} onClick={() => setRaise(v)}>
                    {v}
                  </button>
                ))}
              </div>
            )}
            <div className="row" style={{ gap: 8 }}>
              <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'stay' })}>
                {t('rdg.stay')}
              </button>
              <button className="btn primary grow" disabled={acting || legal.maxRaise < 1 || raise < 1} onClick={() => void act({ type: 'raise', amount: raise })}>
                {t('rdg.raise', { n: raise })}
              </button>
            </div>
          </div>
        )}
        {!betting && !deciding && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {me && me.status === 'BET' ? t('rdg.yourBet', { n: me.bet + me.raise }) : ended ? t('bj.handOver') : dealing ? t('ab.dealing') : view.phase === 'BETTING' ? t('bj.betting') : t('bj.waiting')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
